import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Community, Role, User } from '@shared/types';
import { api } from './api';

interface AuthValue {
  user: User | null;
  community: Community | null;
  loading: boolean;
  refresh: () => Promise<void>;
  setUser: (user: User | null) => void;
  setCommunity: (community: Community | null) => void;
  logout: () => Promise<void>;
  has: (...roles: Role[]) => boolean;
  isBoard: boolean;
  /** Financials are for the Board Member role only; admin and superadmin do not imply it. */
  canUseFinance: boolean;
  isReviewer: boolean;
  isArb: boolean;
  canOrganise: boolean;
  isSuperAdmin: boolean;
  isTenant: boolean;
  isHomeowner: boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [community, setCommunity] = useState<Community | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { user: current, community: currentCommunity } = await api.me();
      setUser(current);
      setCommunity(currentCommunity);
    } catch {
      setUser(null);
      setCommunity(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    await api.logout().catch(() => undefined);
    setUser(null);
    setCommunity(null);
  }, []);

  const value = useMemo<AuthValue>(() => {
    const has = (...roles: Role[]) => {
      if (!user) return false;
      if (roles.includes('superadmin')) return user.roles.includes('superadmin');
      if (user.roles.includes('admin')) return true;
      // A Super Admin managing a community acts as its admin.
      if (user.actingCommunityId && user.roles.includes('superadmin')) return true;
      return roles.some((role) => user.roles.includes(role));
    };
    return {
      user,
      community,
      loading,
      refresh,
      setUser,
      setCommunity,
      logout,
      has,
      isBoard: has('board'),
      canUseFinance: Boolean(
        user && user.communityId && user.roles.includes('board') && !user.roles.includes('superadmin'),
      ),
      isReviewer: has('board', 'arb'),
      isArb: Boolean(user && (user.roles.includes('arb') || user.roles.includes('admin'))),
      canOrganise: has('board', 'organizer'),
      isSuperAdmin: user?.roles.includes('superadmin') === true,
      isTenant: user?.isTenant === true,
      isHomeowner: Boolean(user && !user.isTenant && user.communityId),
    };
  }, [user, community, loading, refresh, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
